import { Button, Card, Flex, Form, Input, Select, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { messageType } from '../../constants';
import { sentGroupMessage, sentMessage } from '../../redux/actions/message';
import { getFileUrl, getUploadUrl, uploadFileToS3 } from '../../redux/actions/upload';
import { getFormattedName, getNewFileName, safeJsonParse } from '../../utils';
import { decodeId } from '../../utils/secureId';
import { errorToast } from '../../utils/toast';
import CustomModal from '../CustomModal';
import PreviewButtons from '../PreviewButtons';

const Template = ({ open, onCancel, isGroup }) => {
    const { userId: encUserId, groupId: group_id } = useParams();
    const groupId = group_id && decodeId(group_id);
    const userId = encUserId && decodeId(encUserId);
    const [form] = Form.useForm();
    const { templates: allTemplates } = useSelector((state) => state.templateSlice);
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [previewContent, setPreviewContent] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const documentRef = useRef(null);
    const mediaRef = useRef(null);
    const videoRef = useRef(null);
    const [uploading, setUploading] = useState({
        doc: false,
        image: false,
        video: false,
    });

    const templates = allTemplates.map((temp) => {
        return {
            ...temp,
            params: safeJsonParse(temp?.params),
            buttons: safeJsonParse(temp?.buttons),
        };
    });

    const selectOptions = templates.map((item) => ({
        label: getFormattedName(item.name),
        value: item.name,
    }));

    const generatePreviewContent = React.useCallback(
        (headerTemplateStr, bodyTemplateStr, buttons, paramsObj) => {
            const generateValues = (section) => {
                const values = [];
                if (paramsObj?.[section]?.length) {
                    paramsObj[section].forEach((param, index) => {
                        if (param.type !== 'text') return;
                        const key = `${section}_${index}`;
                        const value = form.getFieldValue(key) || `{{${values.length + 1}}}`;
                        values.push(value);
                    });
                }
                return values;
            };

            const replaceTemplate = (template, values) => {
                if (typeof template !== 'string') return '';
                return template.replace(/{{(\d+)}}/g, (_, index) => {
                    const i = Number(index) - 1;
                    return values[i] || `{{${index}}}`;
                });
            };

            const headerValues = generateValues('header');
            const bodyValues = generateValues('body');
            const buttonValues = generateValues('button');

            const renderedHeader = replaceTemplate(headerTemplateStr, headerValues);
            const renderedBody = replaceTemplate(bodyTemplateStr, bodyValues);

            const renderedButtons = Array.isArray(buttons)
                ? buttons.map((btn) => {
                    const newBtn = { ...btn };
                    if (newBtn.text) {
                        newBtn.text = replaceTemplate(newBtn.text, buttonValues);
                    }
                    if (newBtn.url) {
                        newBtn.url = replaceTemplate(newBtn.url, buttonValues);
                    }
                    if (newBtn.phone_number) {
                        newBtn.phone_number = replaceTemplate(newBtn.phone_number, buttonValues);
                    }
                    return newBtn;
                })
                : [];

            return {
                header: renderedHeader,
                content: renderedBody,
                buttons: renderedButtons,
            };
        },
        [form]
    );

    const handleTemplateChange = (value) => {
        const selected = templates.find((item) => item.name === value);
        if (selected) {
            const parsedParams = safeJsonParse(selected.params);
            const initialValues = {};
            form.setFieldsValue(initialValues);
            const preview = generatePreviewContent(
                selected?.header ?? '',
                selected?.content ?? '',
                currentTemplate?.buttons ?? [],
                parsedParams
            );

            setCurrentTemplate({ ...selected, params: parsedParams });
            setPreviewContent(preview);
        }
    };

    const renderParams = (paramType) => {
        const paramList = currentTemplate?.params?.[paramType];
        if (!paramList?.length) return null;
        const sectionTitle = `${paramType.charAt(0).toUpperCase() + paramType.slice(1)}`;

        return <div>
            <Typography.Title level={5}>
                {sectionTitle}
            </Typography.Title>
            {paramList?.map((param, index) => {
                const fieldName = `${paramType}_${index}`;
                const { name, type: inputType = 'text', field } = param;
                if (field === false) return null;

                if (inputType === 'document') {
                    return (
                        <React.Fragment>
                            {previewFile ? (
                                <Button block href={previewFile.fileLink} target="_blank">
                                    View Document
                                </Button>
                            ) : (
                                <Button block onClick={() => documentRef.current?.click()} loading={uploading.doc}>
                                    Upload Document
                                </Button>
                            )}
                            <div style={{ display: 'none' }}>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt"
                                    ref={documentRef}
                                    onChange={(e) => handleFileChange(e, 'doc')}
                                />
                            </div>
                        </React.Fragment>
                    );
                }

                if (inputType === 'image') {
                    return (
                        <React.Fragment>
                            {previewFile ? (
                                <Button block href={previewFile.fileLink} target="_blank">
                                    View Image
                                </Button>
                            ) : (
                                <Button block onClick={() => mediaRef.current?.click()} loading={uploading.image}>
                                    Upload Image
                                </Button>
                            )}

                            <div style={{ display: 'none' }}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={mediaRef}
                                    onChange={(e) => handleFileChange(e, 'image')}
                                />
                            </div>
                        </React.Fragment>
                    );
                }

                if (inputType === 'video') {
                    return (
                        <React.Fragment>
                            {previewFile ? (
                                <Button block href={previewFile.fileLink} target="_blank">
                                    View Video
                                </Button>
                            ) : (
                                <Button block onClick={() => videoRef.current?.click()} loading={uploading.video}>
                                    Upload Video
                                </Button>
                            )}

                            <div style={{ display: 'none' }}>
                                <input
                                    type="file"
                                    accept="video/*"
                                    ref={videoRef}
                                    onChange={(e) => handleFileChange(e, 'video')}
                                />
                            </div>
                        </React.Fragment>
                    );
                }

                return (
                    <Form.Item
                        key={fieldName}
                        name={fieldName}
                        rules={[{ required: true, message: `Please enter text for ${name}` }]}
                    >
                        <Input placeholder={`Enter ${name}`} />
                    </Form.Item>
                );
            })}
        </div>
    };

    const handleFileChange = async (event, key) => {
        const selectedFiles = Array.from(event.target.files);
        if (!selectedFiles.length) return;
        setUploading((prev) => ({ ...prev, [key]: true }));

        const allowedDocumentTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
        ];

        const allowedImageTypes = ['image/'];
        const allowedVideoTypes = ['video/'];

        const newFiles = [];

        for (const file of selectedFiles) {
            const { type: mimeType, name } = file;

            if (key === 'doc' && !allowedDocumentTypes.includes(mimeType)) {
                errorToast(`"${name}" is not a supported document type.`);
                continue;
            }

            if (key === 'image' && !allowedImageTypes.some((prefix) => mimeType.startsWith(prefix))) {
                errorToast(`"${name}" is not a supported image type.`);
                continue;
            }

            if (key === 'video' && !allowedVideoTypes.some((prefix) => mimeType.startsWith(prefix))) {
                errorToast(`"${name}" is not a supported video type.`);
                continue;
            }

            newFiles.push({
                file,
                display_name: getNewFileName(file.name),
            });
        }

        if (!newFiles.length) {
            setUploading((prev) => ({ ...prev, [key]: false }));
            return;
        }

        const fileData = newFiles[0];
        try {
            const uploadUrl = await getUploadUrl(
                fileData.file,
                'template',
                userId,
                fileData.display_name
            );
            await uploadFileToS3(uploadUrl, fileData.file, fileData.file.type);
            const fileLink = getFileUrl('template', userId, fileData.display_name);
            setPreviewFile({
                ...fileData,
                fileLink,
            });
        } catch (err) {
            errorToast('Failed to upload the file.');
            console.error(err);
        } finally {
            setUploading((prev) => ({ ...prev, [key]: false }));
            event.target.value = '';
        }
    };

    const renderContent = () => {
        if (!previewContent) return null;

        return (
            <React.Fragment>
                <Card
                    title={
                        <Typography.Title level={5} style={{ margin: 0 }}>
                            {previewContent?.header || 'Template Preview'}
                        </Typography.Title>
                    }
                    styles={{
                        body: { whiteSpace: 'pre-wrap', fontFamily: 'monospace' },
                    }}
                    actions={
                        currentTemplate?.footer && [
                            <Typography.Text type="secondary">{currentTemplate.footer}</Typography.Text>,
                        ]
                    }
                >
                    <Typography.Paragraph style={{ marginBottom: 0 }}>
                        {previewContent?.content}
                    </Typography.Paragraph>
                </Card>
            </React.Fragment>
        );
    };

    const handleSentTemplate = async () => {
        setIsSending(true);

        const values = form.getFieldsValue();

        for (const section of ['header', 'body']) {
            const invalid = currentTemplate?.params?.[section]?.some((param, index) => {
                const paramKey = `${section}_${index}`;
                const inputType = param.type;

                if (inputType === 'document' || inputType === 'image') {
                    if (!previewFile?.file || !previewFile?.fileLink) {
                        errorToast(`Please upload a valid ${inputType} for ${param.name || paramKey}`);
                        return true;
                    }
                }
                return false;
            });

            if (invalid) {
                setIsSending(false);
                return;
            }
        }

        const buildParams = (type) => {
            return (
                currentTemplate?.params?.[type]
                    ?.map((param, index) => {
                        const paramKey = `${type}_${index}`;
                        const { type: inputType } = param;

                        if (inputType === 'document') {
                            if (!previewFile?.fileLink || !previewFile?.display_name) return null;
                            return {
                                type: 'document',
                                document: {
                                    link: previewFile.fileLink,
                                    filename: previewFile?.file?.name,
                                },
                            };
                        }

                        if (inputType === 'image') {
                            if (!previewFile?.fileLink) return null;
                            return {
                                type: 'image',
                                image: {
                                    link: previewFile.fileLink,
                                },
                            };
                        }

                        const text = values[paramKey];
                        if (!text) return null;

                        return {
                            type: 'text',
                            text,
                        };
                    })
                    .filter(Boolean) || []
            );
        };

        const headerParams = buildParams('header');
        const bodyParams = buildParams('body');

        const finalParams = {};
        if (headerParams.length > 0) finalParams.header = headerParams;
        if (bodyParams.length > 0) finalParams.body = bodyParams;
        if (previewContent?.buttons?.length > 0) finalParams.buttons = previewContent.buttons;

        const customParams = {
            ...finalParams,
        };
        if (currentTemplate?.header && !headerParams.length)
            customParams.header = currentTemplate.header;
        if (currentTemplate?.footer) customParams.footer = currentTemplate.footer;

        const templatePayload = {
            id: currentTemplate?.id,
            name: currentTemplate.name,
            ...(Object.keys(finalParams).length > 0 && { params: finalParams }),
            language_code: currentTemplate.language_code,
            customParams,
        };

        const body = {
            contact_id: userId,
            type: messageType.TEMPLATE,
            template: templatePayload,
            text: previewContent?.content,
        };

        if (previewFile?.file && previewFile?.fileLink) {
            body.attachment = {
                name: previewFile.display_name,
                type: previewFile.file.type,
                link: previewFile.fileLink,
                filename: previewFile.file.name,
            };
        }

        const res = isGroup ? await sentGroupMessage(groupId, body) : await sentMessage(body);
        if (res?.ack) onCancel();
        setIsSending(false);
    };

    useEffect(() => {
        if (currentTemplate) {
            const updatedPreview = generatePreviewContent(
                currentTemplate?.header || '',
                currentTemplate?.content,
                currentTemplate?.buttons ?? [],
                currentTemplate?.params
            );
            setPreviewContent(updatedPreview);
        }
    }, [form, currentTemplate, generatePreviewContent]);

    return (
        <CustomModal
            title="Send Template"
            open={open}
            onCancel={onCancel}
            okText="Send"
            onOk={() => form.submit()}
            okButtonProps={{ disabled: !currentTemplate, loading: isSending }}
            width={1000}
        >
            <Form
                layout="vertical"
                form={form}
                size="large"
                onFinish={handleSentTemplate}
                onValuesChange={() => {
                    if (currentTemplate) {
                        const updated = generatePreviewContent(
                            currentTemplate?.header || '',
                            currentTemplate?.content,
                            currentTemplate?.buttons ?? [],
                            currentTemplate?.params
                        );
                        setPreviewContent(updated);
                    }
                }}
            >
                <Form.Item label="Select Template" name="template">
                    <Select
                        placeholder="Choose template"
                        options={selectOptions}
                        onChange={(value) => {
                            form.setFieldsValue({ template: value });
                            handleTemplateChange(value);
                            form.resetFields();
                            form.setFieldsValue({ template: value });

                        }}
                    />
                </Form.Item>

                <Flex gap={24} wrap="wrap" align="start">
                    {currentTemplate?.params && (
                        <div style={{ flex: '1 1 40%' }}>
                            {renderParams('header')}
                            {renderParams('body')}
                            {renderParams('button')}
                        </div>
                    )}

                    <div
                        style={{
                            flex: '1 1 50%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24,
                        }}
                    >
                        {(currentTemplate?.header || currentTemplate?.content) && renderContent()}
                        {previewContent?.buttons && <PreviewButtons buttons={previewContent?.buttons} />}
                    </div>
                </Flex>
            </Form>
        </CustomModal>
    );
};

export default Template;
